import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import type { PhotosFilterTriState } from '@/lib/photos/types/photos-filter-tri-state'
import {
  readStoredPhotosTrayShowFilename,
  writeStoredPhotosTrayShowFilename,
} from '@/lib/photos/photos-tray-show-filename-storage'
import {
  readStoredPhotosTraySort,
  writeStoredPhotosTraySort,
} from '@/lib/photos/photos-tray-sort-storage'
import { type PhotosTraySort, isPhotosTraySort } from '@/lib/photos/sort-tray-photos'

type PhotosTrayViewOptionsInput = {
  albumValue: string | null
  spreadId: string | null
  placedFilter: PhotosFilterTriState
}

/** "Show filename" and Sort for the Photos tray, both persisted to localStorage. */
export function usePhotosTrayViewOptions({
  albumValue,
  spreadId,
  placedFilter,
}: PhotosTrayViewOptionsInput) {
  const queryClient = useQueryClient()
  const [showFileName, setShowFileName] = useState(() => readStoredPhotosTrayShowFilename())
  const [traySort, setTraySort] = useState<PhotosTraySort>(() => readStoredPhotosTraySort())

  const handleTraySortChange = useCallback((nextValue: string) => {
    if (!isPhotosTraySort(nextValue)) return

    setTraySort(nextValue)
    writeStoredPhotosTraySort(nextValue)
  }, [])

  const handleShowFileNameChange = useCallback(
    (checked: boolean) => {
      setShowFileName(checked)
      writeStoredPhotosTrayShowFilename(checked)

      if (!checked && placedFilter !== 'no' && albumValue) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.photos.albumPhotos(albumValue, spreadId),
        })
      }
    },
    [albumValue, placedFilter, queryClient, spreadId]
  )

  return { showFileName, handleShowFileNameChange, traySort, handleTraySortChange }
}
